import React, { useEffect, useState } from 'react';
import { maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { NotificationInfo, PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton/BackToTopButton';
import { CopyBlockContainer } from 'src/components/molecules/CopyBlock/CopyBlockContainer';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { ProductListError } from 'src/components/molecules/ProductListError/ProductListError';
import { ListingsControlsDrawerContainer } from 'src/components/organisms/ListingsControlsDrawer/ListingsControlsDrawerContainer';
import { useFilterNavType } from 'src/hooks/useFilterNavType/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { ProductsState } from 'src/redux/products/types';
import { useStore } from 'src/redux/store';
import * as styles from 'src/styles/pages.styles';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { ListingsLayout } from '../ListingLayout/ListingsLayout';

const {
  publicRuntimeConfig: { CLEAR_PREVIEW_API_URL },
} = getConfig() as NextConfig;

/** Props supplied by the page (GSSP); `TaxonomyListingContainer` merges Redux state fields. */
export type TaxonomyListingOwnProps = Pick<
  PageProps,
  'featureFlags' | 'siteChromeContent' | 'updatedReduxState'
> & {
  preview: boolean;
  stickyTopBanner?: boolean;
};

export type TaxonomyListingProps = TaxonomyListingOwnProps & {
  pageLiveRegionText: string;
  products: ProductsState;
  showProductsList: boolean;
  stickyTopBanner: boolean;
};

export const TaxonomyListing = ({
  pageLiveRegionText,
  preview,
  showProductsList,
  stickyTopBanner = false,
  updatedReduxState,
}: TaxonomyListingProps) => {
  const { query } = useRouter();
  const { dispatch } = useStore();
  const { products } = updatedReduxState as ReduxApplicationState;
  const [redirectNotification, setRedirectNotification] = useState(false);

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      {preview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        {redirectNotification && (
          <div css={[styles.grid, maxWidthContainer]}>
            <NotificationInfo>
              We don&apos;t seem to have that product anymore, but here are some similar items
              below.
            </NotificationInfo>
          </div>
        )}
        {showProductsList ? (
          <ListingsLayout stickyTopBanner={stickyTopBanner}>
            <CopyBlockContainer />
            <MembershipBannerSection trackingLocation="shop" />
            <BackToTopButton />
            <ListingsControlsDrawerContainer />
          </ListingsLayout>
        ) : (
          <ProductListError
            message={products.error ? products.error.message : 'No products found'}
          />
        )}
      </div>
    </>
  );
};
